export const TOPICS = [
  "vin",
  "kunst",
  "musik",
  "poesi",
  "kultur",
  "mad",
  "egnsretter",
  "cykling",
  "levekunst",
  "have",
  "stemme",
  "arkitektur",
  "rejser",
  "haandvaerk",
  "film",
] as const;
export type Topic = typeof TOPICS[number];

export const TOPIC_LABEL: Record<Topic, string> = {
  vin: "Vin",
  kunst: "Kunst",
  musik: "Musik",
  poesi: "Poesi",
  kultur: "Kultur",
  mad: "Mad",
  egnsretter: "Egnsretter",
  cykling: "Cykling",
  levekunst: "Levekunst",
  have: "Have",
  stemme: "Stemme",
  arkitektur: "Arkitektur",
  rejser: "Rejser",
  haandvaerk: "Håndværk",
  film: "Film",
};

export const TOPIC_TAGLINE: Record<Topic, string> = {
  vin: "Smagsnoter, marker, aftener med et glas.",
  kunst: "Udstillinger, essays, kunstnerens blik.",
  musik: "Jazz, folk, singer-songwriter, klassisk – kultur omkring lyden.",
  poesi: "Digte, romaner, forfatterens stemme.",
  kultur: "Langsomt liv, filosofi, tid og nærvær.",
  mad: "Opskrifter som håndværk og glæde.",
  egnsretter: "Retter set gennem deres sted – råvare, kultur, historie.",
  cykling: "Landeveje, ritualer, langsomhed.",
  levekunst: "Stoicisme, adfærd og kunsten at leve godt.",
  have: "Jord, årstider, planter og haveliv.",
  stemme: "Klummer, debat og essays hvor stemmen bærer.",
  arkitektur: "Bygninger, byrum og lys der falder rigtigt.",
  rejser: "Steder set langsomt – essays om at være et sted.",
  haandvaerk: "Snedkeri, keramik, bogbinding – folk der laver noget.",
  film: "Langsom film og fotografi – billeder der holder blikket.",
};

export function isTopic(v: string): v is Topic {
  return (TOPICS as readonly string[]).includes(v);
}

/**
 * Længere SEO-tekster pr. emne – læses som en redaktionel efterskrift i bunden
 * af emnesiden. Skrevet i samme stemme som resten af Flâneriet.
 */
export const TOPIC_SEO_TEXT: Record<Topic, string> = {
  vin: "Under Vin samler Flâneriet tekster om naturvin, klassisk vin og vinkultur – smagsnoter, portrætter af vinbønder, essays om terroir, og små historier om aftener, hvor et glas gjorde forskellen. For den, der drikker langsomt og læser lige så langsomt.",
  kunst: "Kunst hos Flâneriet er udstillinger værd at gå efter, essays om samtidskunst og kunsthistorie, portrætter af kunstnere og den stille betragtnings kunst. En kurateret linje til den, der stadig tror, at et maleri kan holde blikket i et helt kvarter.",
  musik: "Musik dækker jazz, klassisk, reggae, folk og den store singer-songwriter-tradition – Bob Dylan, Leonard Cohen, Tom Waits, The War on Drugs og de stemmer, der ligger i samme stilart. Koncertanmeldelser, portrætter af musikere, essays om lyden af en tid. For den, der lytter én plade igennem frem for at skippe.",
  poesi: "Poesi og litteratur, tæt sammen. Digte, romaner, forfatterportrætter og oplæsninger – fra Inger Christensen og Tove Ditlevsen til det nye, der lige er skrevet. Læsestof til de langsomme timer.",
  kultur: "Kultur her betyder langsomt liv, filosofi, nærvær og tid. Essays om stilhed, om at gøre mindre, om at være tilstede – og om hvorfor det er sværere end nogensinde, og derfor vigtigere.",
  mad: "Mad som håndværk og glæde. Opskrifter, restaurantnoter, portrætter af kokke og bagere, og betragtninger over det at spise langsomt sammen med nogen, man holder af.",
  egnsretter: "Egnsretter er madnørderiet i skæringen mellem råvare, kultur, historie og sted. Cassoulet fra Castelnaudary, ossobuco alla milanese, gumbo fra Louisiana, sønderjysk kaffebord, tsukemono, mole, mezze, klipfisk i Portugal. Rachel Roddy, Claudia Roden, Fuchsia Dunlop, Diana Kennedy, Edna Lewis, Jonathan Nunn – og SK fra Café Eddy som dansk pejlemærke. Én ret fortalt gennem sin egn, ikke en generisk hverdagsopskrift.",
  cykling: "Cykling som livsstil, ikke som konkurrence. Landeveje, ritualer, portrætter af ryttere, kaffestop og æstetikken omkring det at trille afsted uden for meget hast.",
  levekunst: "Levekunst er stoicisme, Epiktet og Marcus Aurelius, men også moderne stemmer om det gode liv. Tekster om adfærd, opmærksomhed og kunsten at leve godt uden at gøre for meget vrøvl om det.",
  have: "Haven som årstidernes langsomme kalender – jord, planter, frø, det der spirer og det der visner. For den, der finder ro i at knæle med hænderne i mulden.",
  stemme: "Stemme samler klummer, debat og essays hvor kvaliteten ligger i den tydelige, personlige stemme og det gode skriftlige håndværk – Knud Romer, Morten Münster, Weekendavisens opinion. For den, der læser lige så meget for hvordan der bliver sagt noget, som for hvad der bliver sagt.",
  arkitektur: "Arkitektur hos Flâneriet er bygninger og rum som håndværk og stemning – ikke som livsstilsporno. Peter Zumthor, danske tegnestuer, gamle huse der har fået lov at ældes, et sidelys der falder ned i et trapperum. Portrætter af arkitekter, essays om byrum og interiør, betragtninger over det at bo et sted.",
  rejser: "Rejser her er ikke destinationsguider, men essays om at være et sted – Fanø i november, en italiensk landsby ved middagstid, en vandretur der tager en hel dag. Flâneur-begrebet kommer bogstaveligt herfra: at drive uden mål gennem en by, en egn, en morgen.",
  haandvaerk: "Håndværk samler snedkeri, keramik, bogbinding, skomageri, bagning som fag – portrætter af mennesker der laver noget med hænderne, og essays om det at gøre én ting godt frem for ti ting hurtigt. Meget dansk-forankret, meget tidsånd nu.",
  film: "Film og fotografi som langsomme billeder – Wenders, Malick, Bergman, dansk ny bølge, dokumentar der tør blive stående ved noget. Anmeldelser, portrætter af instruktører og fotografer, essays om et enkelt billedes lange virkning.",
};
